import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CookieConsent, { getCookieConsentValue, Cookies } from 'react-cookie-consent';
import useTranslation from './useTranslation';
import analyticsService from '../services/analyticsService';

function Analytics({ location }) {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const COOKIE_NAME = 'cookie_consent_analytics';
  const lastPathRef = useRef('');
  const checkTimeoutRef = useRef(null);

  // Funzione per verificare l'esistenza diretta del cookie
  const cookieExists = useCallback(() => {
    return document.cookie.split(';').some(item => {
      const trimmed = item.trim();
      return trimmed.startsWith(`${COOKIE_NAME}=`);
    });
  }, [COOKIE_NAME]);

  // Funzione per ottenere direttamente il valore del cookie
  const getCookieValue = useCallback(() => {
    const match = document.cookie.match('(^|;)\\s*' + COOKIE_NAME + '\\s*=\\s*([^;]+)');
    return match ? match.pop() : undefined;
  }, [COOKIE_NAME]);

  const checkConsent = useCallback(() => {
    if (!analyticsService.enabled) return;

    // Verifica direttamente nel document.cookie
    const exists = cookieExists();
    const directValue = getCookieValue();
    
    // Se il cookie non esiste fisicamente, puliamo sempre
    if (!exists) {
      analyticsService.cleanup();
      setShowBanner(true);
      return;
    }
    
    if (directValue === 'true') {
      analyticsService.init();
      setShowBanner(false);
    } else {
      analyticsService.cleanup();
      setShowBanner(directValue === undefined);
    }
  }, [cookieExists, getCookieValue]);

  // Controllo iniziale
  useEffect(() => {
    checkConsent();
    
    // Monitoraggio della navigazione
    const handleRouteChange = () => {
      // Usiamo un timeout di 50ms per dare tempo al router di completare la navigazione
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
      
      checkTimeoutRef.current = setTimeout(() => {
        // Controllare nuovamente dopo la navigazione
        checkConsent();
      }, 50);
    };

    // Intercettiamo gli eventi nativi di navigazione
    window.addEventListener('popstate', handleRouteChange);
    
    // Monkey patch per history.pushState e history.replaceState
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function() {
      originalPushState.apply(this, arguments);
      handleRouteChange();
    };
    
    window.history.replaceState = function() {
      originalReplaceState.apply(this, arguments);
      handleRouteChange();
    };
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [checkConsent]);

  // Aggiungiamo un listener per il visibilitychange
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkConsent();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkConsent]);

  // Controllo quando cambia la location
  useEffect(() => {
    if (location && location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      checkConsent();
      
      if (cookieExists() && getCookieValue() === 'true') {
        analyticsService.pageView(location.pathname + location.search);
      }
    }
  }, [location, checkConsent, cookieExists, getCookieValue]);

  const handleAccept = useCallback(() => {
    setShowBanner(false);
    analyticsService.init();
  }, []);

  const handleDecline = useCallback(() => {
    setShowBanner(false);
    analyticsService.cleanup();
    Cookies.set(COOKIE_NAME, "false", { 
      path: '/', 
      expires: 365, // Scade dopo un anno
      sameSite: "lax"
    });
  }, [COOKIE_NAME]);

  if (!analyticsService.enabled || !showBanner) {
    return null;
  }

  return (
    <CookieConsent
      location="bottom"
      buttonText={t('cookieAccept')}
      declineButtonText={t('cookieDecline')}
      enableDeclineButton
      cookieName={COOKIE_NAME}
      onAccept={handleAccept}
      onDecline={handleDecline}
      style={{ 
        background: "var(--color-element)", 
        color: "var(--color-text-primary)", 
        padding: "15px", 
        fontSize: "14px",
        boxShadow: "0 -4px 8px rgba(0, 0, 0, 0.1)",
        zIndex: 9999,
        border: "var(--border-width) var(--border-style) var(--color-border)",
        borderRadius: "var(--radius-lg) var(--radius-lg) 0 0",
        bottom: "0",
        left: "0",
        right: "0",
        position: "fixed",
        fontFamily: "var(--font-family)",
        animation: "cookieBannerSlideUp 0.5s ease 0.3s forwards",
        width: "100dvw !important",
        opacity: "0",
        transform: "translateY(100%)"
      }}
      buttonStyle={{ 
        background: "var(--color-primary)", 
        color: "white", 
        fontSize: "14px", 
        padding: "8px 16px", 
        borderRadius: "var(--radius-pill)",
        marginLeft: "15px",
        fontWeight: "500",
        boxShadow: "var(--bs-box-shadow-sm)",
      }}
      declineButtonStyle={{
        background: "var(--color-surface)", 
        color: "var(--color-text-primary)", 
        fontSize: "14px", 
        padding: "8px 16px", 
        borderRadius: "var(--radius-pill)",
        border: "var(--border-width) var(--border-style) var(--color-border)",
        fontWeight: "500",
        boxShadow: "var(--bs-box-shadow-sm)",
      }}
      contentStyle={{ 
        flex: "1 0 280px", 
        margin: "15px" 
      }}
      overlayStyle={{
        background: "rgba(0, 0, 0, 0.5)"
      }}
      disableButtonStyles={false}
      expires={365}
    >
      <style>
        {`
          @keyframes cookieBannerSlideUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <h3 style={{ marginTop: "0", marginBottom: "10px", fontSize: "16px", fontWeight: "600" }}>
        {t('cookieTitle')}
      </h3>
      {t('cookieDescription')}
    </CookieConsent>
  );
}

// Componente wrapper che usa useLocation
function AnalyticsWrapper() {
  try {
    const location = useLocation();
    return <Analytics location={location} />;
  } catch {
    return <Analytics />;
  }
}

export default AnalyticsWrapper; 